<template>
    <div class="Policy">
        <div class="Policy__headerBar" @click="toggle">
            <div class="Policy__requirementTypeLabel">
                <FontAwesomeIcon class="Policy__headerBarToggle" :icon="caretIcon" />
                <FontAwesomeIcon v-if="icon" class="Policy__headerBarIcon" :icon="icon" />
                <EidIcon
                    v-if="showRequirementTypeIcon"
                    class="Policy__requirementIcon"
                    :icon-src="requirementTypeIcon"
                    :default-icon-svg="defaultCoverageIcon"
                    :svg-props="{color: evidentGreenColor}"
                />
                <span class="Policy__headerBarRequirementName">{{ requirementTypeLabel }}</span>
                <span v-if="requirementInstanceLabel" class="Policy__headerBarRequirementInstance">
                    - {{ requirementInstanceLabel }}
                </span>
                <EvaluationRuleBadge v-if="isEvaluationRuleRequirement" class="ml-2" />
            </div>
            <div v-if="showComplianceStatus" class="Policy__complianceStatus">
                <ComplianceStatusBadge
                    class="EntityDetails__headerStatusBadge"
                    :status="requirementDetails.complianceStatus"
                />
            </div>
            <ExceptionBadge
                v-if="isStatusOverridePresent"
                class="EvaluationError__exceptionBadge"
                :label="exceptionLabel"
                :exception-type="requirementDetails.complianceStatusMetadata.statusOverride.type"
                :until="exceptionUntil"
                :applied-to="requirementDetails.complianceStatusMetadata.statusOverride.level"
                :note="requirementDetails.complianceStatusMetadata.statusOverride.note"
                :disable-popover="false"
            />
            <DocumentDataOnlyBadge v-if="isShowingDataOnlyBadge" />
            <DocumentViewButton v-else-if="isShowingDocumentViewButton" class="mr-3" @click.stop="showDocument" />

            <RbacDisplayGuard :features="[RbacFeature.entityDetailsAddRemoveException]">
                <Expandable v-if="hasAnyAction" :menu-config="{ positionX: 'end', teleport: true }">
                    <template #trigger="{ toggle }">
                        <NewButton variant="flat" size="icon-sm" color="secondary">
                            <EidIcon
                                :icon-src="faEllipsisVertical"
                                class="Policy__actionsButton"
                                @click.stop="toggle"
                            />
                        </NewButton>
                    </template>
                    <template #default="{ close }">
                        <ExpandableItem
                            v-if="isStatusOverridePresent"
                            :disabled="hasEntityLevelException"
                            label="Remove Exception"
                            @click="$emit('removeExceptions', [
                                requirementDetails.complianceStatusMetadata.statusOverride.id
                            ]); close();"
                        />
                        <template v-else-if="requirementDetails.complianceStatus === 'NON_COMPLIANT'">
                            <ExpandableItem
                                label="Grant exceptions for entire requirement"
                                @click="grantExceptionForRequirement(); close();"
                            >
                                <div class="flex flex-col">
                                    <span>Grant exceptions for entire requirement</span>
                                    <div class="text-xs text-eidLightGray">
                                        <strong>All current and future non-compliant criteria</strong>
                                        <span> will be waived as compliant. This can be undone.</span>
                                    </div>
                                </div>
                            </ExpandableItem>
                            <ExpandableItem
                                v-if="requirement"
                                label="Grant exceptions for non-compliant criteria only"
                                @click="grantExceptionForNonCompliantCriteria(); close();"
                            >
                                <div class="flex flex-col">
                                    <span>Grant exceptions for non-compliant criteria only</span>
                                    <div class="text-xs text-eidLightGray">
                                        <strong>Current non-compliant criteria</strong>
                                        <span> will be waived as compliant. This can be undone.</span>
                                    </div>
                                </div>
                            </ExpandableItem>
                        </template>
                    </template>
                </Expandable>
            </RbacDisplayGuard>
        </div>
        <div v-if="opened">
            <NoPolicy
                v-if="noPolicy"
                :requirement-details="requirementDetails"
                :requirement-processing="requirementProcessing"
                @grant-exception-for-requirement="grantExceptionForRequirement"
            />
            <PolicyInfo
                v-else-if="isInsuranceRequirements"
                :requirement="requirementWithNonExrtractedFields"
                :requirement-model="requirementModel"
                :evaluation-errors="evaluationErrors"
                :all-requirements="allRequirements"
                :evaluation-results="requirementDetails.evaluationResults"
                :collateral-entities="collateralEntities"
                :non-extraction-results="requirementDetails.nonExtraction"
                @go-to-criterion="goToCriterion"
                @grant-exception-to-criterion="grantExceptionToCriterion"
                @grant-evaluate-entity-exception="grantEvaluateEntityException"
                @remove-exceptions="$emit('removeExceptions', $event)"
            />
            <PolicyInfoGeneric
                v-else
                :requirement="requirementWithNonExrtractedFields"
                :requirement-model="requirementModel"
                :evaluation-errors="evaluationErrors"
                :all-requirements="allRequirements"
                :evaluation-results="requirementDetails.evaluationResults"
                :collateral-entities="collateralEntities"
                :non-extraction-results="requirementDetails.nonExtraction"
                @go-to-criterion="goToCriterion"
                @grant-exception-to-criterion="grantExceptionToCriterion"
                @grant-evaluate-entity-exception="grantEvaluateEntityException"
                @remove-exceptions="$emit('removeExceptions', $event)"
            />
        </div>
    </div>
</template>

<script lang="ts">
    import { PropType } from 'vue';

    import { Component, Prop, Ref, Vue } from '@evidentid/vue-property-decorator';

    import startCase from 'lodash/startCase';

    import { faCaretRight, IconDefinition } from '@fortawesome/free-solid-svg-icons';
    import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
    import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { Menu, MenuLink } from '@evidentid/dashboard-commons/components/Menu';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import {
        TprmCredentialType,
        TprmRequirementCategory,
        TprmRequirementDefinition,
    } from '@evidentid/tprm-portal-lib/models/common';
    import { SubmissionMethod } from '@evidentid/tprm-portal-lib/models/common/SubmissionMethod.model';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { EntityRiskProfile } from '@evidentid/tprm-portal-lib/models/decisioning';
    import {
        CollateralEntity, ComplianceStatus, EntityRequirement, EntityRequirementDetails, ExceptionInput,
        ExceptionLevel, ExceptionType, TprmRequirementModel, TprmRequirementType, VerificationStatus,
    } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac' ;

    import Expandable from '@/components/expandable/Expandable.vue';
    import ExpandableItem from '@/components/expandable-item/ExpandableItem.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import ComplianceStatusBadge from '@/modules/dashboard/components/ComplianceStatusBadge/ComplianceStatusBadge.vue';
    import { DashboardState, useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import { FieldEvaluationResultError } from '@/modules/decisioning-criteria/types';
    import defaultCoverageIcon from '@/modules/entity-details/assets/coverage-icon-default.svg';
    import DocumentDataOnlyBadge
        from '@/modules/entity-details/components/DocumentDataOnlyBadge/DocumentDataOnlyBadge.vue';
    import DocumentViewButton from '@/modules/entity-details/components/DocumentViewButton/DocumentViewButton.vue';
    import EvaluationRuleBadge
        from '@/modules/entity-details/components/EvaluationRuleBadge/EvaluationRuleBadge.vue';
    import ExceptionBadge from '@/modules/entity-details/components/ExceptionBadge/ExceptionBadge.vue';
    import NoPolicy from '@/modules/entity-details/components/NoPolicy/NoPolicy.vue';
    import PolicyInfoGeneric
        from '@/modules/entity-details/components/Policy/sub-components/PolicyInfoGeneric/PolicyInfoGeneric.vue';
    import {
        createCriterionLevelException,
        createRequirementLevelException,
    } from '@/modules/entity-details/utils/createException';
    import { getRequirementTypeIconUri } from '@/modules/entity-details/utils/getRequirementTypeIconUri';
    import {
        isFieldImportApiOrWithoutDocumentUpload,
    } from '@/modules/entity-details/utils/isFieldImportApiOrWithoutDocumentUpload/isFieldImportApiOrWithoutDocumentUpload';
    import { tailwindColors } from '@/styles/variables/tailwind-colors/tailwindColors';

    import PolicyInfo from './sub-components/PolicyInfo/PolicyInfo.vue';
    import { injectNonExtractedFields } from './utils';

    @Component({
        emits: [ 'goToCriterion', 'grantExceptions', 'removeExceptions' ],
        components: {
            PolicyInfoGeneric,
            DocumentDataOnlyBadge,
            DocumentViewButton,
            EidIcon,
            EvaluationRuleBadge,
            ExceptionBadge,
            ExpandableItem,
            Expandable,
            Menu,
            MenuLink,
            NewButton,
            PolicyInfo,
            FontAwesomeIcon,
            ComplianceStatusBadge,
            NoPolicy,
            RbacDisplayGuard,
        },
    })
    export default class Policy extends Vue {
        @Prop({ type: Object, required: true })
        private entity!: Entity;

        @Prop({ type: Object, required: true })
        private requirementDetails!: EntityRequirementDetails;

        @Prop({ type: Array, default: () => [] })
        private allRequirements!: EntityRequirementDetails[];

        @Prop({ default: null })
        private icon!: IconDefinition | null;

        @Prop({ type: Boolean, default: true })
        private showRequirementTypeIcon!: boolean;

        @Prop({ type: Array, default: () => [] })
        private entityRiskProfiles!: EntityRiskProfile[];

        @Prop({ type: Array, default: () => [] })
        private requirementModels!: TprmRequirementModel[];

        @Prop({ type: Array, default: () => [] })
        private credentialTypes!: TprmCredentialType[];

        @Prop({
            type: Object as PropType<Record<string, FieldEvaluationResultError>>,
            default: (): Record<string, FieldEvaluationResultError> => ({}),
        })
        private evaluationErrors!: Record<string, FieldEvaluationResultError>;

        @Prop({ type: Array as PropType<CollateralEntity[]>, default: (): CollateralEntity[] => [] })
        private collateralEntities!: CollateralEntity[];

        private faCaretRight = faCaretRight;
        private faAngleDown = faAngleDown;
        private defaultCoverageIcon = defaultCoverageIcon;
        private evidentGreenColor = tailwindColors.eidTrustGreen.DEFAULT;
        private RbacFeature = RbacFeature;

        private opened: boolean = false;

        private faEllipsisVertical = faEllipsisVertical;
        private ComplianceStatus = ComplianceStatus;

        private get requirementDefinitions(): DashboardState['tprmRequirementsDefinitions'] {
            return (useDashboardStore().$state as DashboardState).tprmRequirementsDefinitions;
        }

        private get requirementDefinition(): TprmRequirementDefinition | null {
            return this.requirementDefinitions[this.requirementDetails.coverageType];
        }

        private get hasValidRequirementForDocumentButtonOrBadge(): boolean {
            return this.requirement?.complianceStatus !== ComplianceStatus.new
                && !this.requirementProcessing;
        }

        private get isFieldImportApiOrWithoutDocumentUploadVal(): boolean {
            return isFieldImportApiOrWithoutDocumentUpload({
                submissionMethod: this.requirement?.submissionMethod,
            });
        }

        // TODO[PRODUCT-24130]: move req def check into isFieldImportApiOrWithoutDocumentUploadVal
        private get isShowingDataOnlyBadge(): boolean {
            return Boolean(
                this.requirement?.submissionMethod === SubmissionMethod.FieldImportApi
                    || !this.requirementDefinition?.hasDocuments,
            );
        }

        private get isShowingDocumentViewButton(): boolean {
            return Boolean(this.requirementDetails.documentsVerificationId);
        }

        private get caretIcon(): IconDefinition {
            return this.opened ? this.faAngleDown : this.faCaretRight;
        }

        private get requirementModel(): TprmRequirementModel | undefined {
            return this.requirementModels
                .find((model) => model.coverageType === this.requirementDetails.coverageType);
        }

        private get requirementTypeLabel(): string {
            return this.requirementModel?.label || startCase(this.requirementDetails.coverageType.toLowerCase());
        }

        private get requirementInstanceLabel(): string | null {
            const credential = this.requirementDetails.instanceId && this.credentialTypes
                .find((credentialType) => credentialType.id === this.requirementDetails.instanceId);
            return credential ? `${credential.name} - ${credential.issuingAuthority}` : null;
        }

        private get requirement(): EntityRequirement | null {
            return this.requirementDetails.coverage || null;
        }

        private get requirementWithNonExrtractedFields(): EntityRequirement | null {
            // Temporary workaround (PRODUCT-25329): when a new criterion is added to a risk profile after the last
            // submission, it becomes non-compliant but its fields were not originally extracted. To avoid confusing UI
            // gaps, we surface those unextracted fields with a non-compliant message until the BE provides
            // a better endpoint that removes the need of FE custom logic.
            const reqData = this.requirementDetails.coverage;
            if (!reqData) {
                return null;
            }
            return injectNonExtractedFields(
                reqData,
                this.requirementDetails.evaluationResults,
                this.requirementDetails.coverageType,
            );
        }

        private get requirementTypeIcon(): string {
            return getRequirementTypeIconUri(this.requirementDetails.coverageType as TprmRequirementType);
        }

        private get hasCoverageLevelException(): boolean {
            const statusOverride = this.requirementDetails.complianceStatusMetadata?.statusOverride;
            const level = statusOverride?.level;
            return Boolean(level && level === ExceptionLevel.requirementInstance);
        }

        private get hasEntityLevelException(): boolean {
            const statusOverride = this.requirementDetails.complianceStatusMetadata?.statusOverride;
            const level = statusOverride?.level;
            return Boolean(level && level === ExceptionLevel.insured);
        }

        private get exceptionLabel(): string {
            return this.hasCoverageLevelException ? 'Exception Granted' : 'Exception';
        }

        private get hasAnyAction(): boolean {
            return Boolean((
                this.requirementDetails.complianceStatusMetadata?.statusOverride ||
                this.requirementDetails.complianceStatus === ComplianceStatus.nonCompliant
            ));
        }

        private get showComplianceStatus(): boolean {
            const hasNonNewStatus = Boolean(this.requirementDetails.complianceStatus) &&
                this.requirementDetails.complianceStatus !== ComplianceStatus.new;
            const hasNonCompliantStatus =
                this.requirementDetails.complianceStatus === ComplianceStatus.nonCompliant;
            const hasEva = this.requirementDetails.evaluationResults?.length > 0;
            if (!hasEva && hasNonCompliantStatus) {
                return true;
            }
            const nonCompliantResults = this.requirementDetails.evaluationResults
                .filter((result) => result.result === ComplianceStatus.nonCompliant);
            const allNonCompliantResultsAreNotUsedInCalculation = nonCompliantResults.length > 0
                && nonCompliantResults.every((result) => !result.useDuringComplianceCalculation);
            if (hasEva && allNonCompliantResultsAreNotUsedInCalculation) {
                return false;
            }
            return hasEva && hasNonNewStatus && !this.requirementProcessing;
        }

        private get isEvaluationRuleRequirement(): boolean {
            return this.entityRiskProfiles.some(
                (group) => group.evaluationRule?.coverageTypes.includes(this.requirementDetails.coverageType),
            );
        }

        private get noPolicy() {
            return this.requirementProcessing ||
                this.requirementDetails.declineReason ||
                this.requirement === null ||
                this.requirement.details?.status === false ||
                this.requirement.details?.coverageStatus === false ||
                this.requirement.details?.presence === false;
        }

        private get requirementProcessing(): boolean {
            if (
                this.entity.complianceStatus === ComplianceStatus.pending
                && [ VerificationStatus.processing, VerificationStatus.actionsReview ]
                    .includes(this.entity.verificationStatus)
            ) {
                return true;
            }

            return !this.requirementDetails.complianceStatus
                && this.requirementDetails.verificationStatus === VerificationStatus.processing;
        }

        private get exceptionUntil(): string | null {
            const exception = this.requirementDetails.complianceStatusMetadata?.statusOverride;
            if (!exception) {
                return null;
            }
            if (exception.type === ExceptionType.untilCoverageExpiration) {
                return this.requirementDetails.coverage?.policy.expirationDate || null;
            } else {
                return exception.until || null;
            }
        }

        private toggle(): void {
            this.opened = !this.opened;
        }

        private showDocument(): void {
            this.$procedures.execute('showEntityRequirementRprDocuments', {
                rpName: this.$rp.current!,
                email: this.entity.contactEmail || '',
                requirementType: this.requirementDetails.coverageType,
                requirementInstanceLabel: this.requirementInstanceLabel,
                requirementInstanceId: this.requirementDetails.instanceId || null,
                entityId: this.entity.id,
                verificationId: this.requirementDetails?.documentsVerificationId,
            });
        }

        private goToCriterion(riskProfileId: string, coverageType: TprmRequirementType): void {
            this.$emit('goToCriterion', riskProfileId, coverageType);
        }

        private grantExceptionForRequirement(): void {
            this.$emit(
                'grantExceptions',
                [ createRequirementLevelException({
                    requirementType: this.requirementDetails.coverageType,
                    instanceId: this.requirementDetails.instanceId,
                }) ],
            );
        }

        private grantEvaluateEntityException(exception: ExceptionInput[]): void {
            this.$emit('grantExceptions', exception);
        }

        private grantExceptionToCriterion(criterionIds: string[]): void {
            this.$emit(
                'grantExceptions',
                criterionIds.map((criterionId) => createCriterionLevelException({
                    requirementType: this.requirementDetails.coverageType,
                    criterionId,
                    instanceId: this.requirementDetails.instanceId,
                })),
            );
        }

        private grantExceptionForNonCompliantCriteria(): void {
            const criterionIds = Object.entries(this.evaluationErrors)
                .filter(([ key, value ]) => (
                    // Criteria spanning multiple fields are keyed by criterion name, not field path.
                    (value.isMultiFieldCriterion || key.includes(this.requirementDetails.coverageType)) &&
                    value.complianceStatusMetadata.length === 0
                ))
                .map(([ _, value ]) =>
                    value.criterionId,
                )
                .reduce((acc, exceptions) => [ ...acc, ...exceptions ], [] as string[]);
            const distinctIds = [ ...new Set(criterionIds) ];
            const exceptions =
                distinctIds.map((criterionId) => createCriterionLevelException({
                    requirementType: this.requirementDetails.coverageType,
                    criterionId,
                    instanceId: this.requirementDetails.instanceId,
                }));
            this.$emit('grantExceptions', exceptions);
        }

        private get isStatusOverridePresent(): boolean {
            return Boolean(this.requirementDetails.complianceStatusMetadata?.statusOverride);
        }

        private get isInsuranceRequirements(): boolean {
            return (useDashboardStore().$state as DashboardState).tprmRequirementsDefinitions?.[
                this.requirementDetails.coverageType
            ]?.category === TprmRequirementCategory.insurance;
        }
    }
</script>
